import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { extractError, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STEPS = ["Profile", "Business Type"];
const CURRENCY_OPTIONS = [
  { value: "INR", label: "Indian Rupee (₹)", symbol: "₹" },
  { value: "USD", label: "US Dollar ($)", symbol: "$" },
  { value: "AUD", label: "Australian Dollar (A$)", symbol: "A$" },
  { value: "EUR", label: "Euro (€)", symbol: "€" },
  { value: "GBP", label: "British Pound (£)", symbol: "£" },
];

export default function Onboarding() {
  const { user, company, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [companyTypes, setCompanyTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    address: "",
    company_type_id: "",
    company_type_name: "",
    timezone: "Asia/Kolkata",
    currency: "INR",
    currency_symbol: "₹",
    tax_rate: 18,
    invoice_prefix: "INV",
  });

  useEffect(() => {
    if (company && company.onboarded) navigate("/app");
  }, [company, navigate]);

  useEffect(() => {
    api.get("/company-types").then((r) => setCompanyTypes(r.data));
  }, []);

  useEffect(() => {
    if (company) {
      setForm((f) => ({
        ...f,
        email: company.email || f.email,
        address: company.address || f.address,
      }));
    }
  }, [company]);

  const update = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const finish = async (companyTypeId, companyTypeName) => {
    setSaving(true);
    try {
      await api.patch("/company", {
        name: company?.name,
        address: form.address,
        phone: company?.phone,
        email: form.email,
        website: "",
        gst_number: "",
        description: "",
        company_type_id: companyTypeId,
        company_type_name: companyTypeName,
        timezone: form.timezone,
        currency: form.currency,
        currency_symbol: form.currency_symbol,
        tax_rate: parseFloat(form.tax_rate) || 0,
        invoice_prefix: form.invoice_prefix,
        onboarded: true,
      });
      
      // Update user name if provided
      if (form.name) {
        await api.patch("/profile", { name: form.name });
      }
      
      await refresh();
      toast.success("Workspace ready");
      navigate("/app");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const selectCategory = async (category) => {
    await finish(category._id, category.name);
  };

  return (
    <div className="min-h-screen bg-[#F5F4F0] py-12 px-6">
      <div className="max-w-2xl mx-auto">
        <div className="font-serif text-3xl tracking-tighter text-[#1A1A1A]">Set up your workspace</div>
        <p className="text-sm text-zinc-600 mt-2">Just a few details to make EventFlow yours.</p>

        <div className="mt-8 flex items-center gap-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1 flex items-center gap-2">
              <div className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-[#D85C38]" : "bg-black/10"}`} />
            </div>
          ))}
        </div>
        <div className="mt-2 text-xs uppercase tracking-[0.2em] text-zinc-500">Step {step + 1} of {STEPS.length} · {STEPS[step]}</div>

        <div className="mt-8 bg-white border border-black/10 rounded-lg p-6 md:p-8">
          {step === 0 && (
            <div className="space-y-4" data-testid="onboarding-step-company">
              <div>
                <Label>Your name</Label>
                <Input className="mt-1.5 h-11" value={form.name} onChange={update("name")} data-testid="onb-name" placeholder="Enter your full name" />
              </div>
              <div>
                <Label>Email (optional)</Label>
                <Input className="mt-1.5 h-11" type="text" value={form.email} onChange={update("email")} data-testid="onb-email" placeholder="your@email.com" />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={(value) => setForm({ ...form, currency: value, currency_symbol: CURRENCY_OPTIONS.find((item) => item.value === value)?.symbol || form.currency_symbol })}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="onb-currency-select"><SelectValue placeholder="Select currency" /></SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((item) => (
                      <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Currency symbol</Label>
                <Input className="mt-1.5 h-11" value={form.currency_symbol} onChange={update("currency_symbol")} data-testid="onb-currency-symbol" placeholder="₹" />
              </div>
              <div>
                <Label>Address (optional)</Label>
                <Textarea className="mt-1.5" rows={2} value={form.address} onChange={update("address")} data-testid="onb-address" placeholder="Your address" />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="onboarding-step-type">
              <Label>Select your business type</Label>
              <div className="grid grid-cols-2 gap-3">
                {companyTypes.map((category) => {
                  const iconMap = {
                    sparkles: "✨",
                    utensils: "🍽️",
                    music: "🎵",
                    brush: "🎨",
                    video: "🎬",
                    camera: "📷",
                    calendar: "📅",
                    flower: "🌸",
                    "flower-2": "💐",
                    users: "👥",
                    "more-horizontal": "···",
                  };
                  return (
                    <button
                      key={category._id}
                      type="button"
                      onClick={() => selectCategory(category)}
                      disabled={saving}
                      className={`flex flex-col items-center gap-2 rounded-lg border px-4 py-6 text-center transition ${
                        saving ? "opacity-50 cursor-not-allowed" : ""
                      } border-black/10 hover:border-[#D85C38] bg-white hover:bg-[#D85C38]/5`}
                      data-testid={`onb-type-${category._id}`}
                    >
                      <span className="text-3xl">{iconMap[category.icon] || "📌"}</span>
                      <span className="font-medium text-sm">{category.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="onb-back-btn">
              Back
            </Button>
            {step === 0 && (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!form.name}
                className="bg-[#1A1A1A] text-white hover:bg-black rounded-full px-6"
                data-testid="onb-next-btn"
              >
                Continue
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
